import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useCurrentUser } from '../context/UserContext';
import { ColorSwatchPicker } from '../components/InlineEditTable';
import DrilldownLayout from '../components/DrilldownLayout';
import SearchableSelect from '../components/SearchableSelect';
import { fmtDate as fmtDeliveryDate } from '../components/ZbSummary';
import { buildStatusGroupMap, buildProgramOptionGroups, closedProgramIdSet, buildComponentOptionGroups, buildGoalOptionGroups } from '../utils/statusGroups';

const API = process.env.REACT_APP_API_URL;

const ROLES = ['admin', 'financial', 'program_manager', 'supporting_program_manager', 'partner'];
// Which Week at a Glance / My Home activity type(s) a Supporting PM should
// be auto-assigned to on any program they support — see backend CLAUDE.md's
// week_board router section (migration 0105). '' = no designator, falls
// back to the original "exactly one supporting PM on this program" rule.
const SUPPORTING_PM_FOCUSES = [
  { value: '', label: '— none —' },
  { value: 'scrub', label: 'Scrub' },
  { value: 'call_review', label: 'Call reviews' },
  { value: 'both', label: 'Both' },
];
const emptyForm = () => ({ cognito_sub: '', name: '', email: '', role: 'partner', supplier_id: '', supporting_pm_focus: '', active: true });

const inputStyle = { padding: '8px', borderRadius: '4px', border: '1px solid #ccc', width: '100%', boxSizing: 'border-box' };
const labelStyle = { fontSize: '12px', color: '#555', marginBottom: '3px', display: 'block' };
const btnStyle = (bg) => ({ padding: '6px 14px', background: bg, color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '13px' });

const ROLE_COLORS = { admin: '#e74c3c', financial: '#3498db', program_manager: '#9b59b6', supporting_program_manager: '#8e6fbf', partner: '#27ae60' };

function fmtDate(iso) {
  if (!iso) return <span style={{ color: '#ccc' }}>—</span>;
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) +
    ' ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

function UserManagementPanel() {
  const [users, setUsers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [form, setForm] = useState(emptyForm());
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const { startImpersonating } = useCurrentUser();

  useEffect(() => {
    fetchUsers();
    axios.get(`${API}/suppliers/`).then(r => setSuppliers(r.data)).catch(() => {});
  }, []);

  const fetchUsers = () => axios.get(`${API}/users/?active_only=false`).then(r => setUsers(r.data));

  const handleSubmit = async (e) => {
    e.preventDefault();
    const data = {
      ...form,
      active: form.active === true || form.active === 'true',
      supplier_id: form.supplier_id ? parseInt(form.supplier_id) : null,
      // '' means "no designator" in the form. On create, that's just null —
      // the field is optional. On update, PUT /users/{id} uses
      // exclude_none=True (can't clear a field by sending null), so an
      // explicit "none" sentinel is what actually clears it server-side —
      // see users.py's update_user().
      supporting_pm_focus: form.supporting_pm_focus || (editing ? 'none' : null),
    };
    if (editing) {
      await axios.put(`${API}/users/${editing}`, data);
      setEditing(null);
    } else {
      await axios.post(`${API}/users/`, data);
    }
    setForm(emptyForm());
    setShowForm(false);
    fetchUsers();
  };

  const handleEdit = (u) => {
    setEditing(u.id);
    setForm({ cognito_sub: u.cognito_sub, name: u.name, email: u.email, role: u.role, supplier_id: u.supplier_id || '', supporting_pm_focus: u.supporting_pm_focus || '', active: u.active });
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this user? They will lose access immediately.')) {
      await axios.delete(`${API}/users/${id}`);
      fetchUsers();
    }
  };

  const cancelForm = () => { setShowForm(false); setEditing(null); setForm(emptyForm()); };

  const thStyle = { padding: '10px 12px', textAlign: 'left', borderBottom: '2px solid #ddd', fontSize: '13px' };
  const tdStyle = { padding: '9px 12px', borderBottom: '1px solid #eee', fontSize: '13px' };

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h2 style={{ margin: 0 }}>User Management</h2>
        <button
          onClick={() => { cancelForm(); setShowForm(true); }}
          style={{ padding: '8px 16px', background: '#2c3e50', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
        >
          + Add User
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} style={{ background: '#f9f9f9', padding: '20px', borderRadius: '8px', marginBottom: '24px', border: '1px solid #eee' }}>
          <h3 style={{ margin: '0 0 16px' }}>{editing ? 'Edit User' : 'New User'}</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '12px', marginBottom: '16px' }}>
            <div>
              <label style={labelStyle}>Full Name *</label>
              <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required style={inputStyle} placeholder="Jane Smith" />
            </div>
            <div>
              <label style={labelStyle}>Email *</label>
              <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required style={inputStyle} placeholder="jane@example.com" />
            </div>
            <div>
              <label style={labelStyle}>Role *</label>
              <select value={form.role} onChange={e => setForm({ ...form, role: e.target.value })} required style={inputStyle}>
                {ROLES.map(r => <option key={r} value={r}>{r.replace(/_/g, ' ')}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Active</label>
              <select value={form.active} onChange={e => setForm({ ...form, active: e.target.value === 'true' })} style={inputStyle}>
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </div>
            {form.role === 'partner' && (
              <div>
                <label style={labelStyle}>Supplier (for partner users)</label>
                <select value={form.supplier_id} onChange={e => setForm({ ...form, supplier_id: e.target.value })} style={inputStyle}>
                  <option value="">None</option>
                  {suppliers.map(s => <option key={s.id} value={s.id}>{s.supplier_name}</option>)}
                </select>
              </div>
            )}
            {form.role === 'supporting_program_manager' && (
              <div>
                <label style={labelStyle}>
                  Week at a Glance focus
                  <span style={{ color: '#aaa', fontWeight: 'normal', marginLeft: '6px' }}>
                    (auto-assigns them to this activity on programs they support)
                  </span>
                </label>
                <select value={form.supporting_pm_focus} onChange={e => setForm({ ...form, supporting_pm_focus: e.target.value })} style={inputStyle}>
                  {SUPPORTING_PM_FOCUSES.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                </select>
              </div>
            )}
            <div style={{ gridColumn: 'span 2' }}>
              <label style={labelStyle}>
                Cognito Sub *
                <span style={{ color: '#aaa', fontWeight: 'normal', marginLeft: '8px' }}>
                  (find this in AWS Cognito console → Users → select user → User attributes → sub)
                </span>
              </label>
              <input value={form.cognito_sub} onChange={e => setForm({ ...form, cognito_sub: e.target.value })} required style={inputStyle} placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" />
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button type="submit" style={btnStyle('#27ae60')}>{editing ? 'Update User' : 'Create User'}</button>
            <button type="button" onClick={cancelForm} style={btnStyle('#95a5a6')}>Cancel</button>
          </div>
        </form>
      )}

      <p style={{ fontSize: '12px', color: '#aaa', margin: '0 0 12px' }}>
        New users are auto-provisioned on first login with role <strong>partner</strong>. Set their role here once they've signed in.
      </p>

      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ background: '#f5f5f5' }}>
            <th style={thStyle}>Name</th>
            <th style={thStyle}>Email</th>
            <th style={thStyle}>Role</th>
            <th style={thStyle}>Status</th>
            <th style={thStyle}>Last Login</th>
            <th style={thStyle}>Cognito Sub</th>
            <th style={thStyle}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {users.map(u => (
            <tr key={u.id} style={{ borderBottom: '1px solid #eee', opacity: u.active ? 1 : 0.5 }}>
              <td style={tdStyle}><strong>{u.name}</strong></td>
              <td style={tdStyle}>{u.email}</td>
              <td style={tdStyle}>
                <span style={{ background: ROLE_COLORS[u.role] || '#95a5a6', color: 'white', borderRadius: '4px', padding: '2px 8px', fontSize: '12px' }}>
                  {u.role.replace(/_/g, ' ')}
                </span>
                {u.role === 'supporting_program_manager' && u.supporting_pm_focus && (
                  <span style={{ marginLeft: '6px', background: '#f3ebf8', color: '#7d3c98', borderRadius: '4px', padding: '2px 7px', fontSize: '11px', fontWeight: 600 }}>
                    {SUPPORTING_PM_FOCUSES.find(f => f.value === u.supporting_pm_focus)?.label || u.supporting_pm_focus}
                  </span>
                )}
              </td>
              <td style={tdStyle}>
                <span style={{ color: u.active ? '#27ae60' : '#e74c3c', fontWeight: '500', fontSize: '13px' }}>
                  {u.active ? 'Active' : 'Inactive'}
                </span>
              </td>
              <td style={{ ...tdStyle, color: '#7f8c8d', fontSize: '12px' }}>{fmtDate(u.last_login_at)}</td>
              <td style={{ ...tdStyle, color: '#aaa', fontSize: '11px', fontFamily: 'monospace' }}>{u.cognito_sub}</td>
              <td style={tdStyle}>
                <button
                  onClick={() => startImpersonating(u.id)}
                  title={`View app as ${u.name}`}
                  style={{ ...btnStyle('#7f8c8d'), marginRight: '6px' }}
                >
                  View As
                </button>
                <button onClick={() => handleEdit(u)} style={{ ...btnStyle('#3498db'), marginRight: '6px' }}>Edit</button>
                <button onClick={() => handleDelete(u.id)} style={btnStyle('#e74c3c')}>Delete</button>
              </td>
            </tr>
          ))}
          {users.length === 0 && (
            <tr><td colSpan={7} style={{ ...tdStyle, textAlign: 'center', color: '#aaa', padding: '40px' }}>No users yet. Add the first user above.</td></tr>
          )}
        </tbody>
      </table>
    </>
  );
}

const ADMIN_SECTIONS = [
  { key: 'users', label: 'User Management', content: <UserManagementPanel /> },
  { key: 'lookup-values', label: 'Lookup Values', description: 'Dropdown options & pill colors', content: <LookupValuesEditor /> },
  { key: 'convertr', label: 'Convertr Duplicate Cleanup', content: <ConvertrDuplicatePanel /> },
  { key: 'delivery-dup-cleanup', label: 'Delivery Duplicate Cleanup', description: 'Remove redundant leads from abandoned re-upload attempts', content: <DeliveryDuplicateCleanupPanel /> },
  { key: 'premature-under-review', label: 'Premature Under Review Repair', description: 'One-time backfill for a since-fixed status bug', content: <PrematureUnderReviewPanel /> },
  { key: 'delivery-status-breakdown', label: 'Delivery Lead Status Breakdown', description: 'Read-only status counts for a delivery', content: <DeliveryStatusBreakdownPanel /> },
  { key: 'linkedin-backfill', label: 'LinkedIn Profile Backfill', description: 'One-time backfill for a since-fixed FIELD_MAP gap', content: <LinkedInBackfillPanel /> },
  { key: 'lead-change-history', label: 'Lead Change History', description: 'Who changed what, and undo a bulk status/QA override', content: <LeadChangeHistoryPanel /> },
  { key: 'task-triggers', label: 'Task Trigger Inventory', description: 'See every My Home task trigger, open counts, and a manual resync', content: <TaskTriggersPanel /> },
  { key: 'qa-reset', label: 'Lead QA Diagnostic & Reset', content: <QAResetPanel /> },
  { key: 'data-management', label: 'Data Management', description: 'Danger zone', content: <DataManagement /> },
];

export default function Admin() {
  const [section, setSection] = useState('users');
  return (
    <div>
      <h2 style={{ margin: '0 0 16px' }}>Admin</h2>
      <DrilldownLayout sections={ADMIN_SECTIONS} selectedKey={section} onSelect={setSection} sidebarLabel="Sections" />
    </div>
  );
}

// Human-readable labels for known categories. Any new category added to the DB
// will still appear automatically using a formatted fallback (e.g. "delivery_status" → "Delivery Status").
const CATEGORY_LABELS = {
  program_status:   'Program Status',
  component_status: 'Component Status',
  goal_status:      'Goal Status',
  lead_status:      'Lead Status',
  delivery_status:  'Delivery Status',
  invoice_status:   'Invoice Status',
};

function categoryLabel(key) {
  return CATEGORY_LABELS[key] || key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function LookupValuesEditor() {
  const [categories, setCategories] = useState([]);   // fetched from API — auto-discovers new ones
  const [category, setCategory] = useState('');
  const [values, setValues] = useState([]);
  const [newValue, setNewValue] = useState('');
  const [newSortOrder, setNewSortOrder] = useState('');
  const [newColor, setNewColor] = useState('#3498db');
  const [newStatusGroup, setNewStatusGroup] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});

  // Load all categories from DB — no hardcoded list, so new categories appear automatically
  useEffect(() => {
    axios.get(`${API}/lookup-values/categories`)
      .then(r => {
        setCategories(r.data);
        if (r.data.length > 0 && !category) setCategory(r.data[0]);
      })
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchValues = useCallback(() => {
    if (!category) return;
    axios.get(`${API}/lookup-values/?category=${category}`).then(r => setValues(r.data)).catch(() => {});
  }, [category]);

  useEffect(() => { fetchValues(); }, [fetchValues]);

  const handleAdd = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/lookup-values/`, { category, value: newValue, sort_order: newSortOrder ? parseInt(newSortOrder) : 0, color: newColor || null, status_group: newStatusGroup || null });
      setNewValue('');
      setNewSortOrder('');
      setNewColor('#3498db');
      setNewStatusGroup('');
      fetchValues();
    } catch (err) {
      alert('Could not add value: ' + (err?.response?.data?.detail || err?.message || 'unknown error'));
    }
  };

  const handleSaveEdit = async (id) => {
    try {
      await axios.put(`${API}/lookup-values/${id}`, { value: editForm.value, sort_order: editForm.sort_order, color: editForm.color ?? null, status_group: editForm.status_group || null });
      setEditingId(null);
      fetchValues();
    } catch (err) {
      alert('Could not save: ' + (err?.response?.data?.detail || err?.message || 'unknown error'));
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this lookup value?')) {
      await axios.delete(`${API}/lookup-values/${id}`);
      fetchValues();
    }
  };

  const thStyle = { padding: '8px 12px', textAlign: 'left', borderBottom: '2px solid #ddd', fontSize: '12px', background: '#f5f5f5' };
  const tdStyle = { padding: '7px 12px', borderBottom: '1px solid #eee', fontSize: '13px' };

  return (
    <div>
      <h3 style={{ margin: '0 0 6px' }}>Lookup Values</h3>
      <p style={{ margin: '0 0 16px', fontSize: '13px', color: '#7f8c8d' }}>
        Manage status and classification options used throughout the app. The <strong>Color</strong> sets the pill color shown for that
        status on the Programs, Components, Goals and Invoices pages. For <code>*_status</code> categories, <strong>Group</strong> controls
        whether that status shows in the always-visible list (Pending/Running) or the collapsed "Closed" section (Closed) on the
        Programs/Components/Goals, PM Home, and Supplier Portal pages — leave unset for non-status categories.
      </p>
      <div style={{ marginBottom: '16px' }}>
        <select value={category} onChange={e => setCategory(e.target.value)} style={{ ...inputStyle, maxWidth: '260px' }}>
          {categories.map(c => <option key={c} value={c}>{categoryLabel(c)}</option>)}
        </select>
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', maxWidth: '680px', marginBottom: '16px' }}>
        <thead>
          <tr>
            <th style={thStyle}>Value</th>
            <th style={thStyle}>Color / Preview</th>
            <th style={thStyle}>Group</th>
            <th style={thStyle}>Sort Order</th>
            <th style={thStyle}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {values.map(v => (
            <tr key={v.id}>
              <td style={tdStyle}>
                {editingId === v.id ? (
                  <input value={editForm.value || ''} onChange={e => setEditForm({ ...editForm, value: e.target.value })} style={{ ...inputStyle, width: 'auto' }} />
                ) : v.value}
              </td>
              <td style={tdStyle}>
                {editingId === v.id ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    {editForm.color ? (
                      <span style={{ alignSelf: 'flex-start', background: editForm.color, color: 'white', borderRadius: '4px', padding: '2px 8px', fontSize: '12px' }}>
                        {editForm.value || v.value}
                      </span>
                    ) : (
                      <span style={{ alignSelf: 'flex-start', background: '#fff', color: '#999', border: '1px dashed #bbb', borderRadius: '4px', padding: '2px 8px', fontSize: '12px' }}>
                        {editForm.value || v.value} · <span style={{ color: '#bbb' }}>no color set (uses page default) — pick a swatch</span>
                      </span>
                    )}
                    <ColorSwatchPicker value={editForm.color} onChange={c => setEditForm({ ...editForm, color: c })} allowNone />
                  </div>
                ) : (
                  v.color ? (
                    <span style={{ background: v.color, color: 'white', borderRadius: '4px', padding: '2px 8px', fontSize: '12px' }}>
                      {v.value}
                    </span>
                  ) : (
                    <span style={{ background: '#fff', color: '#999', border: '1px dashed #bbb', borderRadius: '4px', padding: '2px 8px', fontSize: '12px' }}>
                      {v.value} · <span style={{ color: '#bbb' }}>no color</span>
                    </span>
                  )
                )}
              </td>
              <td style={tdStyle}>
                {editingId === v.id ? (
                  <select value={editForm.status_group || ''} onChange={e => setEditForm({ ...editForm, status_group: e.target.value })} style={{ ...inputStyle, width: 'auto' }}>
                    <option value="">—</option>
                    <option value="pending">Pending</option>
                    <option value="running">Running</option>
                    <option value="closed">Closed</option>
                  </select>
                ) : (v.status_group ? v.status_group[0].toUpperCase() + v.status_group.slice(1) : <span style={{ color: '#ccc' }}>—</span>)}
              </td>
              <td style={tdStyle}>
                {editingId === v.id ? (
                  <input type="number" value={editForm.sort_order ?? ''} onChange={e => setEditForm({ ...editForm, sort_order: parseInt(e.target.value) || 0 })} style={{ ...inputStyle, width: '80px' }} />
                ) : v.sort_order}
              </td>
              <td style={tdStyle}>
                {editingId === v.id ? (
                  <>
                    <button onClick={() => handleSaveEdit(v.id)} style={btnStyle('#27ae60')}>Save</button>
                    <button onClick={() => setEditingId(null)} style={{ ...btnStyle('#95a5a6'), marginLeft: '6px' }}>Cancel</button>
                  </>
                ) : (
                  <>
                    <button onClick={() => { setEditingId(v.id); setEditForm({ value: v.value, sort_order: v.sort_order, category: v.category, color: v.color || null, status_group: v.status_group || '' }); }} style={btnStyle('#3498db')}>Edit</button>
                    <button onClick={() => handleDelete(v.id)} style={{ ...btnStyle('#e74c3c'), marginLeft: '6px' }}>Delete</button>
                  </>
                )}
              </td>
            </tr>
          ))}
          {values.length === 0 && (
            <tr><td colSpan={5} style={{ ...tdStyle, textAlign: 'center', color: '#aaa', padding: '20px' }}>No values for this category.</td></tr>
          )}
        </tbody>
      </table>
      <form onSubmit={handleAdd} style={{ display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
        <div>
          <label style={labelStyle}>Value *</label>
          <input value={newValue} onChange={e => setNewValue(e.target.value)} required style={{ ...inputStyle, width: '200px' }} placeholder="e.g. In Progress" />
        </div>
        <div>
          <label style={labelStyle}>Color</label>
          <div style={{ paddingTop: '4px' }}>
            <ColorSwatchPicker value={newColor} onChange={setNewColor} allowNone />
          </div>
        </div>
        <div>
          <label style={labelStyle}>Group</label>
          <select value={newStatusGroup} onChange={e => setNewStatusGroup(e.target.value)} style={{ ...inputStyle, width: 'auto' }}>
            <option value="">—</option>
            <option value="pending">Pending</option>
            <option value="running">Running</option>
            <option value="closed">Closed</option>
          </select>
        </div>
        <div>
          <label style={labelStyle}>Sort Order</label>
          <input type="number" value={newSortOrder} onChange={e => setNewSortOrder(e.target.value)} style={{ ...inputStyle, width: '80px' }} placeholder="0" />
        </div>
        <button type="submit" style={btnStyle('#27ae60')}>Add Value</button>
      </form>
    </div>
  );
}



function ConvertrDuplicatePanel() {
  const [programs, setPrograms] = useState([]);
  // Closed programs nest into a collapsible section on the Program picker
  // below instead of mixing into the flat list — see src/utils/statusGroups.js.
  const [programStatuses, setProgramStatuses] = useState([]);
  const programOptionGroups = buildProgramOptionGroups(programs, buildStatusGroupMap(programStatuses));
  const [programId, setProgramId] = useState('');
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [result, setResult] = useState(null);
  const [confirmed, setConfirmed] = useState(false);

  useEffect(() => {
    axios.get(`${API}/programs/`).then(r => setPrograms(r.data)).catch(() => {});
    axios.get(`${API}/lookup-values/?category=program_status`).then(r => setProgramStatuses(r.data)).catch(() => {});
  }, []);

  const runPreview = async () => {
    setLoading(true); setPreview(null); setResult(null); setConfirmed(false);
    try {
      const params = programId ? `?program_id=${programId}&dry_run=true` : '?dry_run=true';
      const r = await axios.post(`${API}/admin/delete-convertr-duplicate-leads${params}`);
      setPreview(r.data);
    } catch (e) {
      setPreview({ error: e?.response?.data?.detail || 'Failed' });
    } finally { setLoading(false); }
  };

  const runDelete = async () => {
    setExecuting(true); setResult(null);
    try {
      const params = programId ? `?program_id=${programId}&dry_run=false` : '?dry_run=false';
      const r = await axios.post(`${API}/admin/delete-convertr-duplicate-leads${params}`);
      setResult({ ok: true, msg: r.data.message });
      setPreview(null); setConfirmed(false);
    } catch (e) {
      setResult({ ok: false, msg: e?.response?.data?.detail || 'Delete failed' });
    } finally { setExecuting(false); }
  };

  const tdS = { padding: '5px 10px', fontSize: '12px', borderBottom: '1px solid #eee', fontFamily: 'monospace' };

  return (
    <div>
      <h3 style={{ margin: '0 0 4px', color: '#7d3c98' }}>🔁 Convertr Duplicate Lead Cleanup</h3>
      <p style={{ margin: '0 0 20px', fontSize: '13px', color: '#7f8c8d' }}>
        When Convertr delivers a lead it appends a reference number to the lead ID (e.g. <code>EG-001</code> → <code>EG-001-3356233</code>).
        Re-uploading with those new IDs creates duplicate records. This tool finds and removes the orphaned originals.
      </p>

      <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: '16px' }}>
        <div>
          <div style={labelStyle}>Program (optional — leave blank for all)</div>
          <SearchableSelect value={programId}
            onChange={v => { setProgramId(v); setPreview(null); setResult(null); }}
            options={programOptionGroups.options} closedOptions={programOptionGroups.closedOptions}
            placeholder="— all programs —" />
        </div>
        <button onClick={runPreview} disabled={loading}
          style={btnStyle(loading ? '#aaa' : '#7d3c98')}>
          {loading ? 'Scanning…' : '🔍 Preview Duplicates'}
        </button>
      </div>

      {preview && !preview.error && (
        <div style={{ marginBottom: '16px' }}>
          <div style={{ padding: '10px 14px', background: preview.would_delete > 0 ? '#fef9ec' : '#eafaf1', border: `1px solid ${preview.would_delete > 0 ? '#f0b429' : '#a9dfbf'}`, borderRadius: '6px', marginBottom: '12px', fontSize: '13px' }}>
            {preview.would_delete > 0
              ? `⚠️ Found ${preview.would_delete} orphaned base-ID records to delete.`
              : '✓ No duplicate records found.'}
          </div>

          {preview.would_delete > 0 && (
            <>
              <div style={{ fontSize: '12px', color: '#555', marginBottom: '8px', fontWeight: '600' }}>Sample records that will be deleted (showing up to 10):</div>
              <div style={{ border: '1px solid #dde', borderRadius: '6px', overflow: 'hidden', marginBottom: '14px' }}>
                <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                  <thead>
                    <tr style={{ background: '#2c3e50' }}>
                      {['Lead ID (to delete)', 'Goal ID', 'Status', 'QA Status'].map(h => (
                        <th key={h} style={{ padding: '6px 10px', color: 'white', fontSize: '11px', fontWeight: '600', textAlign: 'left' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {preview.sample.map((r, i) => (
                      <tr key={i} style={{ background: i % 2 ? '#f9f9f9' : 'white' }}>
                        <td style={tdS}>{r.lead_id}</td>
                        <td style={tdS}>{r.goal_id}</td>
                        <td style={tdS}>{r.status}</td>
                        <td style={{ ...tdS, color: r.qa_status === 'Accepted' ? '#1e8449' : r.qa_status === 'Rejected' ? '#c0392b' : '#888' }}>{r.qa_status || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', fontSize: '13px', color: '#c0392b', cursor: 'pointer' }}>
                <input type="checkbox" checked={confirmed} onChange={e => setConfirmed(e.target.checked)} />
                I understand this permanently deletes {preview.would_delete} records — proceed
              </label>
              <button
                onClick={runDelete}
                disabled={!confirmed || executing}
                style={{ padding: '8px 20px', background: confirmed ? '#c0392b' : '#ccc', color: 'white', border: 'none', borderRadius: '4px', cursor: confirmed ? 'pointer' : 'not-allowed', fontSize: '13px' }}
              >
                {executing ? 'Deleting…' : `Delete ${preview.would_delete} Duplicate Records`}
              </button>
            </>
          )}
        </div>
      )}
      {preview?.error && (
        <div style={{ marginBottom: '12px', color: '#c0392b', fontSize: '13px' }}>{preview.error}</div>
      )}
      {result && (
        <div style={{ padding: '10px 14px', background: result.ok ? '#eafaf1' : '#fdedec', border: `1px solid ${result.ok ? '#a9dfbf' : '#f5b7b1'}`, borderRadius: '6px', fontSize: '13px', color: result.ok ? '#1e8449' : '#c0392b' }}>
          {result.ok ? '✓ ' : '✗ '}{result.msg}
        </div>
      )}
    </div>
  );
}

function DeliveryDuplicateCleanupPanel() {
  const [goals, setGoals] = useState([]);
  const [components, setComponents] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [programStatuses, setProgramStatuses] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [goalId, setGoalId] = useState('');
  const [keepDeliveryId, setKeepDeliveryId] = useState('');
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [result, setResult] = useState(null);
  const [confirmed, setConfirmed] = useState(false);

  useEffect(() => {
    axios.get(`${API}/goals/`).then(r => setGoals(r.data || [])).catch(() => {});
    axios.get(`${API}/components/`).then(r => setComponents(r.data || [])).catch(() => {});
    axios.get(`${API}/programs/`).then(r => setPrograms(r.data || [])).catch(() => {});
    axios.get(`${API}/lookup-values/?category=program_status`).then(r => setProgramStatuses(r.data || [])).catch(() => {});
    axios.get(`${API}/supplier-deliveries/admin/all`).then(r => setDeliveries(r.data || [])).catch(() => {});
  }, []);

  // Closed programs nest into a collapsible section on the Goal picker below
  // instead of mixing into the flat list — see src/utils/statusGroups.js.
  const closedProgramIds = closedProgramIdSet(programs, buildStatusGroupMap(programStatuses));
  const componentByIdForGoals = new Map(components.map(c => [c.id, c]));
  const goalOptionGroups = buildGoalOptionGroups(goals, componentByIdForGoals, closedProgramIds);
  const deliveryOptions = deliveries.map(d => ({
    value: d.id,
    label: `#${d.id} — ${d.filename || 'unnamed'} (${d.program_name || '—'}, ${fmtDeliveryDate(d.upload_date)})`,
  }));

  const runPreview = async () => {
    setLoading(true); setPreview(null); setResult(null); setConfirmed(false);
    try {
      const r = await axios.post(`${API}/admin/cleanup-duplicate-delivery-leads?goal_id=${goalId}&keep_delivery_id=${keepDeliveryId}&dry_run=true`);
      setPreview(r.data);
    } catch (e) {
      setPreview({ error: e?.response?.data?.detail || 'Failed' });
    } finally { setLoading(false); }
  };

  const runDelete = async () => {
    setExecuting(true); setResult(null);
    try {
      const r = await axios.post(`${API}/admin/cleanup-duplicate-delivery-leads?goal_id=${goalId}&keep_delivery_id=${keepDeliveryId}&dry_run=false`);
      setResult({ ok: true, msg: r.data.message });
      setPreview(null); setConfirmed(false);
    } catch (e) {
      setResult({ ok: false, msg: e?.response?.data?.detail || 'Delete failed' });
    } finally { setExecuting(false); }
  };

  const tdS = { padding: '5px 10px', fontSize: '12px', borderBottom: '1px solid #eee', fontFamily: 'monospace' };
  const canPreview = goalId && keepDeliveryId;

  return (
    <div>
      <h3 style={{ margin: '0 0 4px', color: '#7d3c98' }}>🧹 Delivery Duplicate Lead Cleanup</h3>
      <p style={{ margin: '0 0 20px', fontSize: '13px', color: '#7f8c8d' }}>
        For a goal where the same batch was uploaded as several separate deliveries instead of resubmitted
        in place (leaving redundant lead records for the same real contacts scattered across deliveries).
        Pick the goal and the delivery whose leads are authoritative — this finds every lead elsewhere in
        that goal sharing an email with the kept delivery's leads.
      </p>

      <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: '16px' }}>
        <SearchableSelect
          label="Goal to clean up"
          options={goalOptionGroups.options}
          closedOptions={goalOptionGroups.closedOptions}
          value={goalId}
          onChange={v => { setGoalId(v); setPreview(null); setResult(null); }}
          placeholder="— search goal —"
        />
        <SearchableSelect
          label="Authoritative delivery (keep this one's leads)"
          options={deliveryOptions}
          value={keepDeliveryId}
          onChange={v => { setKeepDeliveryId(v); setPreview(null); setResult(null); }}
          placeholder="— search delivery —"
        />
        <button onClick={runPreview} disabled={loading || !canPreview}
          style={btnStyle(loading || !canPreview ? '#aaa' : '#7d3c98')}>
          {loading ? 'Scanning…' : '🔍 Preview Duplicates'}
        </button>
      </div>

      {preview && !preview.error && (
        <div style={{ marginBottom: '16px' }}>
          <div style={{ padding: '10px 14px', background: preview.would_delete > 0 ? '#fef9ec' : '#eafaf1', border: `1px solid ${preview.would_delete > 0 ? '#f0b429' : '#a9dfbf'}`, borderRadius: '6px', marginBottom: '12px', fontSize: '13px' }}>
            {preview.would_delete > 0
              ? `⚠️ Found ${preview.would_delete} redundant lead(s) in other deliveries for this goal.`
              : '✓ No redundant leads found.'}
          </div>

          {preview.would_delete > 0 && (
            <>
              <div style={{ fontSize: '12px', color: '#555', marginBottom: '8px', fontWeight: '600' }}>Sample records that will be deleted (showing up to 20):</div>
              <div style={{ border: '1px solid #dde', borderRadius: '6px', overflow: 'hidden', marginBottom: '14px', overflowX: 'auto' }}>
                <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                  <thead>
                    <tr style={{ background: '#2c3e50' }}>
                      {['Lead ID (to delete)', 'Email', 'Delivery ID', 'Status', 'QA Flags'].map(h => (
                        <th key={h} style={{ padding: '6px 10px', color: 'white', fontSize: '11px', fontWeight: '600', textAlign: 'left' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {preview.sample.map((r, i) => (
                      <tr key={i} style={{ background: i % 2 ? '#f9f9f9' : 'white' }}>
                        <td style={tdS}>{r.lead_id}</td>
                        <td style={tdS}>{r.email}</td>
                        <td style={tdS}>{r.delivery_id}</td>
                        <td style={tdS}>{r.status}</td>
                        <td style={{ ...tdS, color: '#c0392b' }}>{(r.qa_flags || []).join('; ') || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', fontSize: '13px', color: '#c0392b', cursor: 'pointer' }}>
                <input type="checkbox" checked={confirmed} onChange={e => setConfirmed(e.target.checked)} />
                I understand this permanently deletes {preview.would_delete} records — proceed
              </label>
              <button
                onClick={runDelete}
                disabled={!confirmed || executing}
                style={{ padding: '8px 20px', background: confirmed ? '#c0392b' : '#ccc', color: 'white', border: 'none', borderRadius: '4px', cursor: confirmed ? 'pointer' : 'not-allowed', fontSize: '13px' }}
              >
                {executing ? 'Deleting…' : `Delete ${preview.would_delete} Duplicate Records`}
              </button>
            </>
          )}
        </div>
      )}
      {preview?.error && (
        <div style={{ marginBottom: '12px', color: '#c0392b', fontSize: '13px' }}>{preview.error}</div>
      )}
      {result && (
        <div style={{ padding: '10px 14px', background: result.ok ? '#eafaf1' : '#fdedec', border: `1px solid ${result.ok ? '#a9dfbf' : '#f5b7b1'}`, borderRadius: '6px', fontSize: '13px', color: result.ok ? '#1e8449' : '#c0392b' }}>
          {result.ok ? '✓ ' : '✗ '}{result.msg}
        </div>
      )}
    </div>
  );
}

function PrematureUnderReviewPanel() {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [result, setResult] = useState(null);
  const [confirmed, setConfirmed] = useState(false);

  const runPreview = async () => {
    setLoading(true); setPreview(null); setResult(null); setConfirmed(false);
    try {
      const r = await axios.post(`${API}/admin/fix-premature-under-review?dry_run=true`);
      setPreview(r.data);
    } catch (e) {
      setPreview({ error: e?.response?.data?.detail || 'Failed' });
    } finally { setLoading(false); }
  };

  const runFix = async () => {
    setExecuting(true); setResult(null);
    try {
      const r = await axios.post(`${API}/admin/fix-premature-under-review?dry_run=false`);
      setResult({ ok: true, msg: r.data.message });
      setPreview(null); setConfirmed(false);
    } catch (e) {
      setResult({ ok: false, msg: e?.response?.data?.detail || 'Fix failed' });
    } finally { setExecuting(false); }
  };

  const tdS = { padding: '5px 10px', fontSize: '12px', borderBottom: '1px solid #eee', fontFamily: 'monospace' };

  return (
    <div>
      <h3 style={{ margin: '0 0 4px', color: '#7d3c98' }}>🩹 Premature Under Review Repair</h3>
      <p style={{ margin: '0 0 20px', fontSize: '13px', color: '#7f8c8d' }}>
        One-time backfill for a since-fixed bug where a lead cleared by a QA re-run was promoted
        straight to "Under Review" even though its delivery had not actually been accepted by a PM yet.
        Finds any lead sitting at "Under Review" whose own delivery isn't "Delivered to PM" — that
        combination can only happen from this bug — and restores it to "Pending Review".
      </p>

      <button onClick={runPreview} disabled={loading}
        style={btnStyle(loading ? '#aaa' : '#7d3c98')}>
        {loading ? 'Scanning…' : '🔍 Preview Affected Leads'}
      </button>

      {preview && !preview.error && (
        <div style={{ marginTop: '16px' }}>
          <div style={{ padding: '10px 14px', background: preview.would_fix > 0 ? '#fef9ec' : '#eafaf1', border: `1px solid ${preview.would_fix > 0 ? '#f0b429' : '#a9dfbf'}`, borderRadius: '6px', marginBottom: '12px', fontSize: '13px' }}>
            {preview.would_fix > 0
              ? `⚠️ Found ${preview.would_fix} lead(s) prematurely sitting at Under Review.`
              : '✓ No affected leads found.'}
          </div>

          {preview.would_fix > 0 && (
            <>
              <div style={{ fontSize: '12px', color: '#555', marginBottom: '8px', fontWeight: '600' }}>Sample records that will be restored to Pending Review (showing up to 20):</div>
              <div style={{ border: '1px solid #dde', borderRadius: '6px', overflow: 'hidden', marginBottom: '14px', overflowX: 'auto' }}>
                <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                  <thead>
                    <tr style={{ background: '#2c3e50' }}>
                      {['Lead ID', 'Email', 'Delivery ID', 'Goal ID', 'QA Status'].map(h => (
                        <th key={h} style={{ padding: '6px 10px', color: 'white', fontSize: '11px', fontWeight: '600', textAlign: 'left' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {preview.sample.map((r, i) => (
                      <tr key={i} style={{ background: i % 2 ? '#f9f9f9' : 'white' }}>
                        <td style={tdS}>{r.lead_id}</td>
                        <td style={tdS}>{r.email}</td>
                        <td style={tdS}>{r.delivery_id}</td>
                        <td style={tdS}>{r.goal_id}</td>
                        <td style={tdS}>{r.qa_status || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', fontSize: '13px', color: '#7d3c98', cursor: 'pointer' }}>
                <input type="checkbox" checked={confirmed} onChange={e => setConfirmed(e.target.checked)} />
                Restore these {preview.would_fix} lead(s) to Pending Review
              </label>
              <button
                onClick={runFix}
                disabled={!confirmed || executing}
                style={btnStyle(confirmed ? '#7d3c98' : '#ccc')}
              >
                {executing ? 'Fixing…' : `Fix ${preview.would_fix} Lead(s)`}
              </button>
            </>
          )}
        </div>
      )}
      {preview?.error && (
        <div style={{ marginTop: '12px', color: '#c0392b', fontSize: '13px' }}>{preview.error}</div>
      )}
      {result && (
        <div style={{ marginTop: '16px', padding: '10px 14px', background: result.ok ? '#eafaf1' : '#fdedec', border: `1px solid ${result.ok ? '#a9dfbf' : '#f5b7b1'}`, borderRadius: '6px', fontSize: '13px', color: result.ok ? '#1e8449' : '#c0392b' }}>
          {result.ok ? '✓ ' : '✗ '}{result.msg}
        </div>
      )}
    </div>
  );
}

function TaskTriggersPanel() {
  const [triggers, setTriggers] = useState(null);
  const [error, setError] = useState(null);
  const [resyncing, setResyncing] = useState(false);
  const [result, setResult] = useState(null);

  const load = () => {
    setError(null);
    axios.get(`${API}/home/triggers`)
      .then(r => setTriggers(r.data))
      .catch(e => setError(e?.response?.data?.detail || 'Failed to load triggers'));
  };
  useEffect(() => { load(); }, []);

  const runResync = async () => {
    setResyncing(true); setResult(null);
    try {
      const r = await axios.post(`${API}/home/triggers/resync`);
      setResult({ ok: true, msg: `Resynced ${r.data.programs_synced} program(s).` });
      load();
    } catch (e) {
      setResult({ ok: false, msg: e?.response?.data?.detail || 'Resync failed' });
    } finally { setResyncing(false); }
  };

  const shapeLabel = { per_occurrence: 'One per occurrence', queue_depth: 'One per open count', read_time: 'Computed live (no stored item)' };

  return (
    <div>
      <h3 style={{ margin: '0 0 4px', color: '#2c3e50' }}>🔔 Task Trigger Inventory</h3>
      <p style={{ margin: '0 0 20px', fontSize: '13px', color: '#7f8c8d' }}>
        Every condition that can auto-generate a My Home / Week at a Glance task, and how many are
        currently open company-wide. These are code-defined (see <code>app/utils/home_triggers.py</code>'s
        <code> TRIGGER_REGISTRY</code>) — this panel gives visibility and a manual re-run, not a no-code
        rule builder; adding a genuinely new trigger condition is still a backend change.
      </p>

      <button onClick={runResync} disabled={resyncing}
        style={btnStyle(resyncing ? '#aaa' : '#2c3e50')}
        title="Runs every trigger below against every program company-wide right now, instead of waiting for its own next natural mutation (upload/accept/QA rerun/etc). Use this after a trigger's logic changes, or to backfill data that predates this feature.">
        {resyncing ? 'Resyncing…' : '🔄 Resync All Programs Now'}
      </button>

      {result && (
        <div style={{ marginTop: '10px', padding: '8px 12px', borderRadius: '6px', fontSize: '12.5px', background: result.ok ? '#eafaf1' : '#fdedec', border: `1px solid ${result.ok ? '#a9dfbf' : '#f5b7b1'}`, color: result.ok ? '#1e8449' : '#c0392b' }}>
          {result.msg}
        </div>
      )}

      {error && <div style={{ marginTop: '16px', color: '#c0392b', fontSize: '13px' }}>{error}</div>}

      {triggers && (
        <table style={{ marginTop: '18px', borderCollapse: 'collapse', width: '100%' }}>
          <thead>
            <tr>
              {['Trigger', 'Activity Type', 'Shape', 'Open Now'].map(h => (
                <th key={h} style={{ padding: '7px 10px', textAlign: h === 'Open Now' ? 'right' : 'left', borderBottom: '2px solid #ddd', fontSize: '11px', color: '#7f8c8d', textTransform: 'uppercase', letterSpacing: '0.03em' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {triggers.map(t => (
              <tr key={t.key}>
                <td style={{ padding: '9px 10px', borderBottom: '1px solid #eee', fontSize: '13px' }}>
                  <div style={{ fontWeight: '600', color: '#2c3e50' }}>{t.label}</div>
                  <div style={{ fontSize: '12px', color: '#95a5a6', marginTop: '2px' }}>{t.description}</div>
                </td>
                <td style={{ padding: '9px 10px', borderBottom: '1px solid #eee', fontSize: '12.5px' }}>{t.activity_type}</td>
                <td style={{ padding: '9px 10px', borderBottom: '1px solid #eee', fontSize: '12.5px', color: '#7f8c8d' }}>{shapeLabel[t.shape] || t.shape}</td>
                <td style={{ padding: '9px 10px', borderBottom: '1px solid #eee', fontSize: '13px', textAlign: 'right', fontWeight: t.open_count > 0 ? '700' : '400', color: t.open_count > 0 ? '#e67e22' : '#bbb' }}>
                  {t.open_count}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function LinkedInBackfillPanel() {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [result, setResult] = useState(null);
  const [confirmed, setConfirmed] = useState(false);

  const runPreview = async () => {
    setLoading(true); setPreview(null); setResult(null); setConfirmed(false);
    try {
      const r = await axios.post(`${API}/admin/backfill-linkedin-profile?dry_run=true`);
      setPreview(r.data);
    } catch (e) {
      setPreview({ error: e?.response?.data?.detail || 'Failed' });
    } finally { setLoading(false); }
  };

  const runFix = async () => {
    setExecuting(true); setResult(null);
    try {
      const r = await axios.post(`${API}/admin/backfill-linkedin-profile?dry_run=false`);
      setResult({ ok: true, msg: r.data.message });
      setPreview(null); setConfirmed(false);
    } catch (e) {
      setResult({ ok: false, msg: e?.response?.data?.detail || 'Fix failed' });
    } finally { setExecuting(false); }
  };

  const tdS = { padding: '5px 10px', fontSize: '12px', borderBottom: '1px solid #eee', fontFamily: 'monospace' };

  return (
    <div>
      <h3 style={{ margin: '0 0 4px', color: '#7d3c98' }}>🔗 LinkedIn Profile Backfill</h3>
      <p style={{ margin: '0 0 20px', fontSize: '13px', color: '#7f8c8d' }}>
        One-time backfill for a since-fixed FIELD_MAP gap: a supplier file headered its LinkedIn column
        "LinkedIn URL" / "LinkedIn" / "LinkedIn Link" instead of "LinkedIn Profile" — the only previously
        accepted spelling — so the value fell through to the unmapped raw_data overflow instead of landing
        in the lead's LinkedIn field. Not lost, just stranded; this recovers it losslessly.
      </p>

      <button onClick={runPreview} disabled={loading}
        style={btnStyle(loading ? '#aaa' : '#7d3c98')}>
        {loading ? 'Scanning…' : '🔍 Preview Affected Leads'}
      </button>

      {preview && !preview.error && (
        <div style={{ marginTop: '16px' }}>
          <div style={{ padding: '10px 14px', background: preview.would_fix > 0 ? '#fef9ec' : '#eafaf1', border: `1px solid ${preview.would_fix > 0 ? '#f0b429' : '#a9dfbf'}`, borderRadius: '6px', marginBottom: '12px', fontSize: '13px' }}>
            {preview.would_fix > 0
              ? `⚠️ Found ${preview.would_fix} lead(s) with a LinkedIn value stranded in raw data.`
              : '✓ No affected leads found.'}
          </div>

          {preview.would_fix > 0 && (
            <>
              <div style={{ fontSize: '12px', color: '#555', marginBottom: '8px', fontWeight: '600' }}>Sample records that will be backfilled (showing up to 20):</div>
              <div style={{ border: '1px solid #dde', borderRadius: '6px', overflow: 'hidden', marginBottom: '14px', overflowX: 'auto' }}>
                <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                  <thead>
                    <tr style={{ background: '#2c3e50' }}>
                      {['Lead ID', 'Email', 'Delivery ID', 'Goal ID', 'LinkedIn Profile'].map(h => (
                        <th key={h} style={{ padding: '6px 10px', color: 'white', fontSize: '11px', fontWeight: '600', textAlign: 'left' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {preview.sample.map((r, i) => (
                      <tr key={i} style={{ background: i % 2 ? '#f9f9f9' : 'white' }}>
                        <td style={tdS}>{r.lead_id}</td>
                        <td style={tdS}>{r.email}</td>
                        <td style={tdS}>{r.delivery_id}</td>
                        <td style={tdS}>{r.goal_id}</td>
                        <td style={tdS}>{r.linkedin_profile}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', fontSize: '13px', color: '#7d3c98', cursor: 'pointer' }}>
                <input type="checkbox" checked={confirmed} onChange={e => setConfirmed(e.target.checked)} />
                Backfill linkedin_profile for these {preview.would_fix} lead(s)
              </label>
              <button
                onClick={runFix}
                disabled={!confirmed || executing}
                style={btnStyle(confirmed ? '#7d3c98' : '#ccc')}
              >
                {executing ? 'Fixing…' : `Fix ${preview.would_fix} Lead(s)`}
              </button>
            </>
          )}
        </div>
      )}
      {preview?.error && (
        <div style={{ marginTop: '12px', color: '#c0392b', fontSize: '13px' }}>{preview.error}</div>
      )}
      {result && (
        <div style={{ marginTop: '16px', padding: '10px 14px', background: result.ok ? '#eafaf1' : '#fdedec', border: `1px solid ${result.ok ? '#a9dfbf' : '#f5b7b1'}`, borderRadius: '6px', fontSize: '13px', color: result.ok ? '#1e8449' : '#c0392b' }}>
          {result.ok ? '✓ ' : '✗ '}{result.msg}
        </div>
      )}
    </div>
  );
}

function DeliveryStatusBreakdownPanel() {
  const [deliveryId, setDeliveryId] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const sel = { padding: '7px 10px', borderRadius: '4px', border: '1px solid #ccc', fontSize: '13px', width: '160px' };

  const runCheck = async () => {
    setLoading(true); setData(null);
    try {
      const r = await axios.get(`${API}/admin/delivery-lead-status-breakdown?delivery_id=${deliveryId}`);
      setData(r.data);
    } catch (e) {
      setData({ error: e?.response?.data?.detail || 'Failed' });
    } finally { setLoading(false); }
  };

  return (
    <div>
      <h3 style={{ margin: '0 0 4px', color: '#7d3c98' }}>🔎 Delivery Lead Status Breakdown</h3>
      <p style={{ margin: '0 0 20px', fontSize: '13px', color: '#7f8c8d' }}>
        Read-only. Delivery cards only ever surface Rejected/Pending Review counts — this shows every
        status a delivery's leads are actually sitting at, useful when those two numbers don't add up
        to the delivery's total row count.
      </p>

      <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', marginBottom: '16px' }}>
        <div>
          <div style={labelStyle}>Delivery ID</div>
          <input type="number" value={deliveryId} onChange={e => { setDeliveryId(e.target.value); setData(null); }} style={sel} placeholder="e.g. 95" />
        </div>
        <button onClick={runCheck} disabled={loading || !deliveryId}
          style={btnStyle(loading || !deliveryId ? '#aaa' : '#7d3c98')}>
          {loading ? 'Checking…' : '🔍 Check'}
        </button>
      </div>

      {data && !data.error && (
        <div>
          <div style={{ fontSize: '13px', color: '#555', marginBottom: '10px' }}>
            Delivery status: <strong>{data.delivery_status || '—'}</strong> · Total leads: <strong>{data.total}</strong>
          </div>
          <div style={{ border: '1px solid #dde', borderRadius: '6px', overflow: 'hidden', maxWidth: '400px' }}>
            <table style={{ borderCollapse: 'collapse', width: '100%' }}>
              <thead>
                <tr style={{ background: '#2c3e50' }}>
                  <th style={{ padding: '6px 10px', color: 'white', fontSize: '11px', fontWeight: '600', textAlign: 'left' }}>Lead.status</th>
                  <th style={{ padding: '6px 10px', color: 'white', fontSize: '11px', fontWeight: '600', textAlign: 'right' }}>Count</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(data.by_status).map(([status, count], i) => (
                  <tr key={status} style={{ background: i % 2 ? '#f9f9f9' : 'white' }}>
                    <td style={{ padding: '6px 10px', fontSize: '13px' }}>{status}</td>
                    <td style={{ padding: '6px 10px', fontSize: '13px', textAlign: 'right', fontFamily: 'monospace' }}>{count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {data?.error && (
        <div style={{ color: '#c0392b', fontSize: '13px' }}>{data.error}</div>
      )}
    </div>
  );
}

const ACTION_LABELS = {
  qa_override: 'QA override (single lead)',
  bulk_qa_override: 'Bulk QA override',
  bulk_status_update: 'Bulk status update (Check Feedback)',
  accept_qa_statuses: 'Accept QA statuses',
  revert: 'Revert',
};

function fmtBatchDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso.endsWith('Z') ? iso : iso + 'Z');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) +
    ' ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

function LeadChangeHistoryPanel() {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyBatch, setBusyBatch] = useState(null);
  const [result, setResult] = useState(null);

  const load = () => {
    setLoading(true);
    axios.get(`${API}/audit-log/lead-batches`).then(r => setBatches(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const revert = async (batch) => {
    const who = batch.user?.name || 'someone';
    if (!window.confirm(
      `Undo this batch of ${batch.affected_count} lead(s) changed by ${who} on ${fmtBatchDate(batch.created_at)}?\n\n` +
      `Each lead's status/QA override will be restored to what it was right before this batch ran. ` +
      `Any lead touched again since won't be touched — it'll be reported as skipped instead.`
    )) return;
    setBusyBatch(batch.batch_id);
    setResult(null);
    try {
      const r = await axios.post(`${API}/audit-log/lead-batches/${batch.batch_id}/revert`);
      setResult({ batchId: batch.batch_id, ...r.data });
      load();
    } catch (e) {
      setResult({ batchId: batch.batch_id, error: e?.response?.data?.detail || 'Revert failed' });
    } finally {
      setBusyBatch(null);
    }
  };

  return (
    <div>
      <h3 style={{ margin: '0 0 4px', color: '#7d3c98' }}>🕘 Lead Change History</h3>
      <p style={{ margin: '0 0 20px', fontSize: '13px', color: '#7f8c8d' }}>
        Every bulk (or single-lead) status/QA override write, newest first. Undo replays each lead's
        values from right before that batch ran — a lead changed again by something else since won't
        be touched, it's reported as skipped instead. Built after a 2026-08-17 incident where a bulk
        override accidentally reset 113 already-Client-Accepted Everpure leads with no way to see who
        did it or what they'd been before.
      </p>

      {loading ? (
        <div style={{ fontSize: '13px', color: '#999' }}>Loading…</div>
      ) : batches.length === 0 ? (
        <div style={{ fontSize: '13px', color: '#999' }}>No bulk lead changes recorded yet.</div>
      ) : (
        <div style={{ border: '1px solid #dde', borderRadius: '6px', overflow: 'hidden' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%' }}>
            <thead>
              <tr style={{ background: '#2c3e50' }}>
                <th style={{ padding: '6px 10px', color: 'white', fontSize: '11px', fontWeight: '600', textAlign: 'left' }}>When</th>
                <th style={{ padding: '6px 10px', color: 'white', fontSize: '11px', fontWeight: '600', textAlign: 'left' }}>Who</th>
                <th style={{ padding: '6px 10px', color: 'white', fontSize: '11px', fontWeight: '600', textAlign: 'left' }}>Action</th>
                <th style={{ padding: '6px 10px', color: 'white', fontSize: '11px', fontWeight: '600', textAlign: 'right' }}>Leads</th>
                <th style={{ padding: '6px 10px', color: 'white', fontSize: '11px', fontWeight: '600', textAlign: 'left' }}>Sample</th>
                <th style={{ padding: '6px 10px', color: 'white', fontSize: '11px', fontWeight: '600', textAlign: 'center' }}></th>
              </tr>
            </thead>
            <tbody>
              {batches.map((b, i) => (
                <tr key={b.batch_id} style={{ background: i % 2 ? '#f9f9f9' : 'white' }}>
                  <td style={{ padding: '6px 10px', fontSize: '13px' }}>{fmtBatchDate(b.created_at)}</td>
                  <td style={{ padding: '6px 10px', fontSize: '13px' }}>{b.user?.name || <span style={{ color: '#ccc' }}>—</span>}</td>
                  <td style={{ padding: '6px 10px', fontSize: '13px' }}>{ACTION_LABELS[b.action] || b.action}</td>
                  <td style={{ padding: '6px 10px', fontSize: '13px', textAlign: 'right', fontFamily: 'monospace' }}>{b.affected_count}</td>
                  <td style={{ padding: '6px 10px', fontSize: '12px', color: '#7f8c8d' }}>
                    {b.sample_leads.map(l => l.lead_id || l.id).join(', ')}{b.affected_count > b.sample_leads.length ? '…' : ''}
                  </td>
                  <td style={{ padding: '6px 10px', textAlign: 'center' }}>
                    {b.reverted ? (
                      <span style={{ fontSize: '12px', color: '#999' }}>Reverted</span>
                    ) : b.action === 'revert' ? (
                      <span style={{ fontSize: '12px', color: '#999' }}>—</span>
                    ) : (
                      <button onClick={() => revert(b)} disabled={busyBatch === b.batch_id}
                        style={{ ...btnStyle(busyBatch === b.batch_id ? '#aaa' : '#c0392b'), padding: '4px 10px', fontSize: '12px' }}>
                        {busyBatch === b.batch_id ? 'Reverting…' : 'Undo'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {result && (
        <div style={{ marginTop: '16px', padding: '10px 14px', borderRadius: '6px', fontSize: '13px', background: result.error ? '#fdecea' : '#eafaf1', color: result.error ? '#c0392b' : '#1e8449' }}>
          {result.error ? (
            <>Failed: {result.error}</>
          ) : (
            <>
              Reverted {result.reverted_count} lead(s).
              {result.skipped?.length > 0 && (
                <div style={{ marginTop: '6px', color: '#7f8c8d' }}>
                  Skipped {result.skipped.length}: {result.skipped.map(s => `${s.lead_id} (${s.reason})`).join('; ')}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function QAResetPanel() {
  const [programs, setPrograms] = useState([]);
  // Closed programs nest into a collapsible section on the Program picker
  // below instead of mixing into the flat list — see src/utils/statusGroups.js.
  const [programStatuses, setProgramStatuses] = useState([]);
  const programStatusGroupMap = buildStatusGroupMap(programStatuses);
  const programOptionGroups = buildProgramOptionGroups(programs, programStatusGroupMap);
  const closedProgramIds = closedProgramIdSet(programs, programStatusGroupMap);
  const [components, setComponents] = useState([]);
  const [goals, setGoals] = useState([]);
  const componentOptionGroups = buildComponentOptionGroups(components, closedProgramIds);
  const componentByIdForGoals = new Map(components.map(c => [c.id, c]));
  const goalOptionGroups = buildGoalOptionGroups(goals, componentByIdForGoals, closedProgramIds);
  const [scope, setScope] = useState('program'); // program | component | goal
  const [programId, setProgramId] = useState('');
  const [componentId, setComponentId] = useState('');
  const [goalId, setGoalId] = useState('');
  const [clearOverrides, setClearOverrides] = useState(true);
  const [breakdown, setBreakdown] = useState(null);
  const [loadingDiag, setLoadingDiag] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [resetResult, setResetResult] = useState(null);
  const [confirmReset, setConfirmReset] = useState(false);

  useEffect(() => {
    axios.get(`${API}/programs/`).then(r => setPrograms(r.data)).catch(() => {});
    axios.get(`${API}/components/`).then(r => setComponents(r.data)).catch(() => {});
    axios.get(`${API}/goals/`).then(r => setGoals(r.data)).catch(() => {});
    axios.get(`${API}/lookup-values/?category=program_status`).then(r => setProgramStatuses(r.data)).catch(() => {});
  }, []);

  const entityId = scope === 'program' ? programId : scope === 'component' ? componentId : goalId;
  const paramKey = scope === 'program' ? 'program_id' : scope === 'component' ? 'component_id' : 'goal_id';

  const runDiagnostic = async () => {
    if (!entityId) return;
    setLoadingDiag(true); setBreakdown(null); setResetResult(null);
    try {
      const r = await axios.get(`${API}/admin/qa-status-breakdown?${paramKey}=${entityId}`);
      setBreakdown(r.data);
    } catch (e) {
      setBreakdown({ error: e?.response?.data?.detail || 'Failed to load' });
    } finally { setLoadingDiag(false); }
  };

  const runReset = async () => {
    if (!entityId || !confirmReset) return;
    setResetting(true); setResetResult(null);
    try {
      const r = await axios.post(`${API}/admin/reset-lead-qa?${paramKey}=${entityId}&clear_overrides=${clearOverrides}`);
      setResetResult({ ok: true, msg: r.data.message });
      setBreakdown(null); setConfirmReset(false);
    } catch (e) {
      setResetResult({ ok: false, msg: e?.response?.data?.detail || 'Reset failed' });
    } finally { setResetting(false); }
  };

  const sel = { padding: '7px 10px', borderRadius: '4px', border: '1px solid #ccc', fontSize: '13px', minWidth: '220px' };
  const thS = { padding: '6px 10px', background: '#2c3e50', color: 'white', fontSize: '11px', fontWeight: '600', textAlign: 'right', whiteSpace: 'nowrap' };
  const tdS = { padding: '5px 10px', fontSize: '12px', textAlign: 'right', borderBottom: '1px solid #eee' };

  return (
    <div>
      <h3 style={{ margin: '0 0 4px', color: '#1a6da8' }}>🔍 Lead QA Status Diagnostic & Reset</h3>
      <p style={{ margin: '0 0 20px', fontSize: '13px', color: '#7f8c8d' }}>
        Investigate raw vs. effective QA status counts for a program/component/goal. Use Reset to clear QA data so leads can be re-processed from scratch.
      </p>

      {/* Scope selector */}
      <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '16px' }}>
        <div>
          <div style={labelStyle}>Scope</div>
          <select value={scope} onChange={e => { setScope(e.target.value); setBreakdown(null); setResetResult(null); setConfirmReset(false); }} style={sel}>
            <option value="program">Program</option>
            <option value="component">Component</option>
            <option value="goal">Goal</option>
          </select>
        </div>
        {scope === 'program' && (
          <div>
            <div style={labelStyle}>Program</div>
            <SearchableSelect value={programId}
              onChange={v => { setProgramId(v); setBreakdown(null); }}
              options={programOptionGroups.options} closedOptions={programOptionGroups.closedOptions}
              placeholder="— select —" />
          </div>
        )}
        {scope === 'component' && (
          <div>
            <div style={labelStyle}>Component</div>
            <SearchableSelect value={componentId}
              onChange={v => { setComponentId(v); setBreakdown(null); }}
              options={componentOptionGroups.options} closedOptions={componentOptionGroups.closedOptions}
              placeholder="— select —" />
          </div>
        )}
        {scope === 'goal' && (
          <div>
            <div style={labelStyle}>Goal</div>
            <SearchableSelect value={goalId}
              onChange={v => { setGoalId(v); setBreakdown(null); }}
              options={goalOptionGroups.options} closedOptions={goalOptionGroups.closedOptions}
              placeholder="— select —" />
          </div>
        )}
        <div style={{ paddingTop: '18px' }}>
          <button onClick={runDiagnostic} disabled={!entityId || loadingDiag}
            style={btnStyle(entityId ? '#1a6da8' : '#aaa')}>
            {loadingDiag ? 'Loading…' : '🔍 Run Diagnostic'}
          </button>
        </div>
      </div>

      {/* Diagnostic results */}
      {breakdown && !breakdown.error && (
        <div style={{ marginBottom: '20px', border: '1px solid #dde', borderRadius: '6px', overflow: 'hidden' }}>
          <div style={{ padding: '8px 12px', background: '#2c3e50', color: 'white', fontSize: '12px', fontWeight: '700' }}>
            DIAGNOSTIC — {breakdown.goals.length} goal(s) in scope · {breakdown.totals.total} total leads
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ borderCollapse: 'collapse', width: '100%' }}>
              <thead>
                <tr>
                  <th style={{ ...thS, textAlign: 'left' }}>Goal</th>
                  <th style={thS}>Total</th>
                  <th style={{ ...thS, background: '#888' }}>Raw: Pending</th>
                  <th style={{ ...thS, background: '#888' }}>Raw: Under Review</th>
                  <th style={{ ...thS, background: '#888' }}>Raw: QA Accepted</th>
                  <th style={{ ...thS, background: '#888' }}>Raw: QA Rejected</th>
                  <th style={{ ...thS, background: '#7d3c98' }}>Override Accept</th>
                  <th style={{ ...thS, background: '#7d3c98' }}>Override Reject</th>
                  <th style={{ ...thS, background: '#2471a3' }}>Delivery Date Set</th>
                  <th style={{ ...thS, background: '#2471a3' }}>Status=Client Accepted</th>
                </tr>
              </thead>
              <tbody>
                {breakdown.goals.map(g => (
                  <tr key={g.goal_id} style={{ background: '#fafafa' }}>
                    <td style={{ ...tdS, textAlign: 'left', fontWeight: '600', color: '#2c3e50' }}>{g.goal_name || `Goal ${g.goal_id}`}</td>
                    <td style={tdS}>{g.total_leads}</td>
                    <td style={{ ...tdS, color: '#888' }}>{g.raw_qa_status.pending}</td>
                    <td style={{ ...tdS, color: '#b7950b' }}>{g.raw_qa_status.under_review}</td>
                    <td style={{ ...tdS, color: '#1e8449', fontWeight: '600' }}>{g.raw_qa_status.accepted}</td>
                    <td style={{ ...tdS, color: '#c0392b' }}>{g.raw_qa_status.rejected}</td>
                    <td style={{ ...tdS, color: '#7d3c98' }}>{g.overrides.override_accept}</td>
                    <td style={{ ...tdS, color: '#7d3c98' }}>{g.overrides.override_reject}</td>
                    <td style={{ ...tdS, color: '#2471a3' }}>{g.client_delivery.delivery_date_set}</td>
                    <td style={{ ...tdS, color: '#2471a3' }}>{g.client_delivery.status_client_accepted}</td>
                  </tr>
                ))}
                <tr style={{ background: '#eaf0fb', fontWeight: '700' }}>
                  <td style={{ ...tdS, textAlign: 'left', color: '#2c3e50' }}>TOTAL</td>
                  <td style={tdS}>{breakdown.totals.total}</td>
                  <td style={{ ...tdS, color: '#888' }}>{breakdown.totals.raw_pending}</td>
                  <td style={{ ...tdS, color: '#b7950b' }}>{breakdown.totals.raw_under_review}</td>
                  <td style={{ ...tdS, color: '#1e8449' }}>{breakdown.totals.raw_qa_accepted}</td>
                  <td style={{ ...tdS, color: '#c0392b' }}>{breakdown.totals.raw_qa_rejected}</td>
                  <td style={{ ...tdS, color: '#7d3c98' }}>{breakdown.totals.override_accept}</td>
                  <td style={{ ...tdS, color: '#7d3c98' }}>{breakdown.totals.override_reject}</td>
                  <td style={{ ...tdS, color: '#2471a3' }}>{breakdown.totals.client_delivery_date_set}</td>
                  <td style={{ ...tdS, color: '#2471a3' }}>{breakdown.totals.status_client_accepted}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div style={{ padding: '8px 12px', background: '#f8f9fa', fontSize: '11px', color: '#888' }}>
            <strong>Raw</strong> = values stored directly in DB · <strong>Override</strong> = PM manual overrides (applied on top of raw) · Columns are not mutually exclusive
          </div>
        </div>
      )}
      {breakdown?.error && (
        <div style={{ marginBottom: '16px', color: '#c0392b', fontSize: '13px' }}>{breakdown.error}</div>
      )}

      {/* Reset section */}
      <div style={{ background: '#fff9f9', border: '1px solid #f5b7b1', borderRadius: '6px', padding: '16px', maxWidth: '560px' }}>
        <div style={{ fontWeight: '700', color: '#c0392b', marginBottom: '8px', fontSize: '13px' }}>⚠️ Reset QA Status</div>
        <p style={{ margin: '0 0 12px', fontSize: '12px', color: '#555' }}>
          Clears <code>qa_status</code>, <code>qa_flags</code>, and <code>qa_ran_at</code> for all leads in the selected scope.
          After resetting, use <strong>Backfill QA</strong> or <strong>Re-run All</strong> on the Lead QA Review page to re-process.
        </p>
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', fontSize: '13px', cursor: 'pointer' }}>
          <input type="checkbox" checked={clearOverrides} onChange={e => setClearOverrides(e.target.checked)} />
          Also clear PM overrides (<code>qa_override</code> + notes)
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px', fontSize: '13px', cursor: 'pointer', color: '#c0392b' }}>
          <input type="checkbox" checked={confirmReset} onChange={e => setConfirmReset(e.target.checked)} />
          I understand this is permanent — reset QA data for the selected {scope}
        </label>
        <button
          onClick={runReset}
          disabled={!entityId || !confirmReset || resetting}
          style={{ padding: '8px 20px', background: confirmReset && entityId ? '#c0392b' : '#ccc', color: 'white', border: 'none', borderRadius: '4px', cursor: confirmReset && entityId ? 'pointer' : 'not-allowed', fontSize: '13px' }}
        >
          {resetting ? 'Resetting…' : 'Reset QA Status'}
        </button>
        {resetResult && (
          <div style={{ marginTop: '10px', fontSize: '13px', color: resetResult.ok ? '#1e8449' : '#c0392b' }}>
            {resetResult.ok ? '✓ ' : '✗ '}{resetResult.msg}
          </div>
        )}
      </div>
    </div>
  );
}

function DataManagement() {
  const [tableMeta, setTableMeta]   = useState([]);
  const [counts, setCounts]         = useState({});
  const [selected, setSelected]     = useState(new Set());
  const [wiping, setWiping]         = useState(false);
  const [result, setResult]         = useState('');

  const load = useCallback(() => {
    axios.get(`${process.env.REACT_APP_API_URL}/admin/table-counts`)
      .then(r => {
        setTableMeta(r.data.tables);
        setCounts(r.data.counts);
        // Pre-check all by default
        setSelected(new Set(r.data.tables.map(t => t.key)));
      })
      .catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleAll = (checked) =>
    setSelected(checked ? new Set(tableMeta.map(t => t.key)) : new Set());

  const toggle = (key) =>
    setSelected(prev => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; });

  const handleWipe = async () => {
    const list = tableMeta.filter(t => selected.has(t.key)).map(t => t.label).join(', ');
    if (!window.confirm(`This will permanently delete all data from:\n\n${list}\n\nThis cannot be undone. Continue?`)) return;
    setWiping(true);
    setResult('');
    try {
      const res = await axios.post(`${process.env.REACT_APP_API_URL}/admin/wipe-tables`, { tables: [...selected] });
      setResult(`✓ Wiped: ${res.data.wiped.join(', ')}`);
      load(); // refresh counts
    } catch (err) {
      setResult(`Error: ${err.response?.data?.detail || err.message}`);
    } finally {
      setWiping(false);
    }
  };

  if (!tableMeta.length) return null;

  const allChecked = tableMeta.length > 0 && tableMeta.every(t => selected.has(t.key));

  return (
    <div>
      <h3 style={{ margin: '0 0 6px', color: '#c0392b' }}>⚠️ Data Management</h3>
      <p style={{ margin: '0 0 20px', fontSize: '13px', color: '#7f8c8d' }}>
        Select tables to empty. This is permanent and cannot be undone.
      </p>

      <div style={{ background: '#fdfefe', border: '1px solid #e8ecef', borderRadius: '8px', padding: '20px', maxWidth: '520px' }}>
        <div style={{ marginBottom: '14px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '13px', fontWeight: '600', color: '#555', cursor: 'pointer' }}>
            <input type="checkbox" checked={allChecked} onChange={e => toggleAll(e.target.checked)} />
            Select / deselect all
          </label>
        </div>

        <div style={{ borderTop: '1px solid #eee', paddingTop: '12px' }}>
          {tableMeta.map(t => (
            <label key={t.key} style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px', cursor: 'pointer' }}>
              <input type="checkbox" checked={selected.has(t.key)} onChange={() => toggle(t.key)} />
              <span style={{ flex: 1, fontSize: '13px', color: t.key === 'users' ? '#c0392b' : '#2c3e50' }}>{t.label}</span>
              <span style={{ fontSize: '12px', color: '#aaa', fontFamily: 'monospace', minWidth: '60px', textAlign: 'right' }}>
                {(counts[t.key] ?? '…').toLocaleString()} rows
              </span>
            </label>
          ))}
        </div>

        <div style={{ marginTop: '16px', display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button
            onClick={handleWipe}
            disabled={wiping || selected.size === 0}
            style={{ padding: '8px 20px', background: selected.size === 0 ? '#ccc' : '#c0392b', color: 'white', border: 'none', borderRadius: '4px', cursor: selected.size === 0 ? 'not-allowed' : 'pointer', fontSize: '13px' }}
          >
            {wiping ? 'Wiping…' : `Wipe ${selected.size} table${selected.size !== 1 ? 's' : ''}`}
          </button>
          {result && (
            <span style={{ fontSize: '13px', color: result.startsWith('✓') ? '#27ae60' : '#e74c3c' }}>{result}</span>
          )}
        </div>
      </div>
    </div>
  );
}
